<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList">
        <div v-if="showDateFilter">
          <ReportFilterPopover
            headerTitle="Optimiser Filter"
            :showApplyButton="true"
            :showResetButton="true"
            :appendToBody="true"
            alignment="left"
            placement="bottom-end"
            @apply="$emit('dateApplied')"
            @reset="$emit('dateReset')">
            <template v-slot:popover>
              <ReportDateRangeSelect 
                :startDate="editingStartDate"
                :endDate="editingEndDate"
                @updateStartDate="editingStartDate = $event; $emit('updateEditingStartDate', $event)"
                @updateEndDate="editingEndDate = $event; $emit('updateEditingEndDate', $event)"
              />
            </template>
            <template v-slot:filterDisplay>
              <div>
                <i class="far fa-calendar mr-2"></i>{{startDate | formatDate}} - {{endDate | formatDate}}
              </div>                
            </template>
          </ReportFilterPopover>
        </div>
        <StaticDataTable class="static-table-list" v-if="hasChartData" :smallRows="true">
          <template v-slot:header>
            <tr>
              <th width="60%">{{optiValueTableData.nameTitle}}</th>
              <th width="40%">{{optiValueTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of optiValueTableData.data" :key="index">
              <td>{{dataValue.name}}</td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="optiValueSeries"
          :legend="optiValueLegend"
          :options="optiValueOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>



<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'

const OptimiserSheetChartTypes = {
  activeSheets: 'activeSheets',
  completedSheets: 'completedSheets'
}

export default {
  name: 'OptimiserSheetChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    showDateFilter: Boolean,
    dateRangeType: {
      type: String,
      default: ChartDateRangeTypes.today
    },
    startDate: String,
    endDate: String
  },
  OptimiserSheetChartTypes: OptimiserSheetChartTypes,
  data() {
    return {
      optiValueSeries: [],
      optiValueTableData: {},
      optiValueLegend: [],
      optiValueOptions: {},
      editingStartDate: this.startDate,
      editingEndDate: this.endDate
    }
  },
  computed: {
    hasChartData() {
      if(this.optiValueSeries) {
        return this.optiValueSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    }
  },
  mounted() {
    this.requestChartData()

    this.subscribeEndpoint('/production/sheetschedule', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/sheetschedule')
  },
  watch: {
    dateRangeType: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.editingStartDate = this.startDate
      this.requestChartData()
    },
    endDate: function() {
      this.editingEndDate = this.endDate
      this.requestChartData()
    }
  },
  methods: {
    requestChartData() {
      var filterData = {
        startDate: this.calculateStartDateForDateRangeType(this.dateRangeType, this.startDate).toDate().toISOString(),
        endDate: this.calculateEndDateForDateRangeType(this.dateRangeType, this.endDate).toDate().toISOString()
      }

      this.$apiService.analyticsService.getProductionOptimiserSummary(filterData)
      .then(response => {
        let fieldStatList = [
          { field: 'totalUsedArea', name: 'Used Area' },
          { field: 'totalOffcutArea', name: 'Offcut Area' },
          { field: 'totalDefectArea', name: 'Defect Area' },
          { field: 'totalWasteArea', name: 'Waste Area' },
        ]

        this.optiValueOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${Utils.formatDecimal(param.value, 2)}m&sup2; (${param.percent}%)`
          }
        }

        if(this.type == OptimiserSheetChartTypes.activeSheets) {
          this.optiValueSeries = [{
            name: 'Active Sheets',
            data: fieldStatList.map(fieldInfo => {
              return {
                name: fieldInfo.name,
                value: Utils.roundNumber(response.data.activeSheets[fieldInfo.field], 2)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.optiValueLegend = {
            data: fieldStatList.map(fieldInfo => fieldInfo.name)
          }

          this.optiValueTableData = {
            nameTitle: 'Active Sheets',
            valueTitle: 'Area',
            data: fieldStatList.map(fieldInfo => {
              return {
                name: fieldInfo.name,
                value: response.data.activeSheets[fieldInfo.field],
                displayValue: Utils.roundNumber(response.data.activeSheets[fieldInfo.field], 2) + 'm&sup2;'
              }
            })
          }

          this.optiValueTableData.data = _.sortBy(this.optiValueTableData.data, 'value').reverse()
        }

        if(this.type == OptimiserSheetChartTypes.completedSheets) {
          if(response.data.completedSheets.totalSheetCount == 0) {
            this.optiValueSeries = []
            this.optiValueLegend = {}
            this.optiValueTableData = {}
            return
          }

          this.optiValueSeries = [{
            name: 'Completed Sheets',
            data: fieldStatList.map(fieldInfo => {
              return {
                name: fieldInfo.name,
                value: Utils.roundNumber(response.data.completedSheets[fieldInfo.field] || 0, 2)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.optiValueLegend = {
            data: fieldStatList.map(fieldInfo => fieldInfo.name)
          }

          this.optiValueTableData = {
            nameTitle: `Data`,
            valueTitle: 'Value',
            data: fieldStatList.map(fieldInfo => {
              return {
                name: fieldInfo.name,
                value: response.data.completedSheets[fieldInfo.field],
                displayValue: Utils.roundNumber(response.data.completedSheets[fieldInfo.field] || 0, 2) + 'm&sup2;'
              }
            })
          }

          this.optiValueTableData.data = _.sortBy(this.optiValueTableData.data, 'value').reverse()

          this.optiValueTableData.data.push({
            name: 'Completed Sheets',
            displayValue: response.data.completedSheets.totalSheetCount
          })
          this.optiValueTableData.data.push({
            name: 'Panel Count',
            displayValue: response.data.completedSheets.totalPanelCount
          })
          this.optiValueTableData.data.push({
            name: 'Offcut Count',
            displayValue: response.data.completedSheets.totalOffcutCount
          })
          this.optiValueTableData.data.push({
            name: 'Blank Panel Count',
            displayValue: response.data.completedSheets.totalBlankPanelCount
          })
        }
      })
    }
  }
}


</script>